import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API } from '../services/api';
import './DailySales.css';

function DailySales() {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStore, setSelectedStore] = useState('all');
  const [stores, setStores] = useState([]);

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Load stores list
  useEffect(() => {
    API.getStores()
      .then(storeList => {
        setStores(storeList);
      })
      .catch(err => console.error('Error loading stores:', err));
  }, []);

  // Load daily sales data
  useEffect(() => {
    if (!startDate || !endDate) return;
    
    setLoading(true);
    const storeFilter = selectedStore === 'all' ? null : selectedStore;
    
    API.getDailySales(null, storeFilter, startDate, endDate)
      .then(data => {
        // Group by store_code and sum amounts
        const storeTotals = {};
        
        data.forEach(record => {
          const store = record.store_code;
          if (!storeTotals[store]) {
            storeTotals[store] = {
              store_code: store,
              catering_amount: 0,
              non_catering_amount: 0,
              total_amount: 0,
              catering_orders: 0,
              non_catering_orders: 0,
              total_orders: 0
            };
          }
          storeTotals[store].catering_amount += parseFloat(record.catering_amount) || 0;
          storeTotals[store].non_catering_amount += parseFloat(record.non_catering_amount) || 0;
          storeTotals[store].total_amount += parseFloat(record.total_amount) || 0;
          storeTotals[store].catering_orders += parseInt(record.catering_orders) || 0;
          storeTotals[store].non_catering_orders += parseInt(record.non_catering_orders) || 0;
          storeTotals[store].total_orders += parseInt(record.total_orders) || 0;
        });
        
        // Convert to array and sort by total_amount descending
        const chartData = Object.values(storeTotals).sort((a, b) => b.total_amount - a.total_amount);
        setSalesData(chartData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading daily sales:', err);
        setLoading(false);
      });
  }, [startDate, endDate, selectedStore]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return <div className="component-loading">Loading daily sales data...</div>;
  }

  if (salesData.length === 0) {
    return (
      <div className="daily-sales">
        <div className="sales-header">
          <h3>Daily Sales Comparison</h3>
          <div className="sales-controls">
            <label>
              Start Date:
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-selector"
              />
            </label>
            <label>
              End Date:
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-selector"
              />
            </label>
            <label>
              Store:
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                className="store-selector"
              >
                <option value="all">All Stores</option>
                {stores.map(store => (
                  <option key={store} value={store}>{store}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="no-data">No sales data available for the selected period</div>
      </div>
    );
  }

  return (
      <div className="daily-sales">
      <div className="sales-header">
        <h3>Daily Sales Comparison</h3>
        <div className="sales-controls">
          <label>
            Start Date:
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-selector"
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-selector"
            />
          </label>
          <label>
            Store:
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="store-selector"
            >
              <option value="all">All Stores</option>
              {stores.map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="sales-charts">
        {/* Sales Amount Chart */}
        <div className="chart-container">
          <h4>Sales Amount by Store</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={salesData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="store_code" 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                labelStyle={{ color: '#333' }}
              />
              <Legend />
              <Bar dataKey="catering_amount" fill="#8884d8" name="Catering" />
              <Bar dataKey="non_catering_amount" fill="#82ca9d" name="Non-Catering" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Table */}
      <div className="sales-summary">
        <h4>Summary</h4>
        <table className="sales-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Catering Amount</th>
              <th>Non-Catering Amount</th>
              <th>Total Amount</th>
              <th>Catering Orders</th>
              <th>Non-Catering Orders</th>
              <th>Total Orders</th>
            </tr>
          </thead>
          <tbody>
            {salesData.map((store, idx) => (
              <tr key={idx}>
                <td className="store-name">{store.store_code}</td>
                <td className="amount">{formatCurrency(store.catering_amount)}</td>
                <td className="amount">{formatCurrency(store.non_catering_amount)}</td>
                <td className="amount total">{formatCurrency(store.total_amount)}</td>
                <td className="orders">{store.catering_orders.toLocaleString()}</td>
                <td className="orders">{store.non_catering_orders.toLocaleString()}</td>
                <td className="orders total">{store.total_orders.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DailySales;

